"use client"

import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { type BooruPost } from "@/lib/booru-api"
import { useGooncaveStore } from "@/lib/gooncave-store"
import { Aperture, BookOpen, Camera, Download, Folder, FolderArchive, FolderPlus, Frame, Grid3X3, Heart, HeartHandshake, Info, LayoutList, Loader2, MoveVertical, PenTool, PictureInPicture2, PlayCircle, Presentation, ScrollText, Share2, Shuffle, SlidersHorizontal, Spline, SquaresFour, Tags, Users, WaveSine, X } from "@/lib/icons"
import { useScriptLinkingStore } from "@/lib/script-linking-store"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import type React from "react"

// Extracted from components/booru-detail-modal.tsx (pure move: same JSX, same behaviour).
interface TopToolbarProps {
  activeScriptId: string | null
  activeScriptName: string | null
  activeVideoUrl: string | null
  bountySelectionActive: boolean
  capturedFrameDataUrl: string | null
  checkIsFavorite: (postId: string) => boolean
  closeModalSurfaces: () => void
  commissionSelectionActive: boolean
  effectiveAlbumImages: string[]
  fetchedDoujinsTitle: string | null
  getEnrichedPost: any
  handleAddToBountySelection: () => void
  handleAddToCommissionSelection: () => void
  handleAddToScript: () => void
  handleCaptureFrame: () => Promise<void>
  handleClearFrame: () => void
  handleDownload: () => Promise<void>
  handleDownloadAllAsZip: () => Promise<void>
  handleFavoriteClick: () => void
  handleFindSimilar: (action: "current" | "new") => void
  handleOpenPip: () => void
  handleReverseSearch: (service: string) => void
  handleFindSource: () => void
  handleShare: () => Promise<void>
  hideUI: boolean
  imageDisplayMode: "contain" | "cover"
  isAddToScriptsDropdownOpen: boolean
  isAlbum: boolean
  isCapturing: boolean
  isFindSimilarDropdownOpen: boolean
  isGoonfolioDropdownOpen: boolean
  isLinkingMode: boolean
  isMediaVideo: boolean
  isResolvingGalleryContent: boolean
  isSlideshowActive: boolean
  isZipDownloading: boolean
  linkingTargetIsGooncave: boolean
  openEditorsAfterSelection: () => void
  post: BooruPost | null
  setImageDisplayMode: React.Dispatch<React.SetStateAction<"contain" | "cover">>
  setIsAddToScriptsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsFindSimilarDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsGoonfolioDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsSlideshowActive: React.Dispatch<React.SetStateAction<boolean>>
  setShowInfo: React.Dispatch<React.SetStateAction<boolean>>
  setShowSaveToGoonfolio: React.Dispatch<React.SetStateAction<boolean>>
  setSlideshowInterval: React.Dispatch<React.SetStateAction<number>>
  setSlideshowShuffle: React.Dispatch<React.SetStateAction<boolean>>
  showInfo: boolean
  slideshowInterval: number
  slideshowShuffle: boolean
  topToolbarRef: React.RefObject<HTMLDivElement | null>
  zipDownloadProgress: number
}

export function TopToolbar({
  activeScriptId,
  activeScriptName,
  activeVideoUrl,
  bountySelectionActive,
  capturedFrameDataUrl,
  checkIsFavorite,
  closeModalSurfaces,
  commissionSelectionActive,
  effectiveAlbumImages,
  fetchedDoujinsTitle,
  getEnrichedPost,
  handleAddToBountySelection,
  handleAddToCommissionSelection,
  handleAddToScript,
  handleCaptureFrame,
  handleClearFrame,
  handleDownload,
  handleDownloadAllAsZip,
  handleFavoriteClick,
  handleFindSimilar,
  handleOpenPip,
  handleReverseSearch,
  handleFindSource,
  handleShare,
  hideUI,
  imageDisplayMode,
  isAddToScriptsDropdownOpen,
  isAlbum,
  isCapturing,
  isFindSimilarDropdownOpen,
  isGoonfolioDropdownOpen,
  isLinkingMode,
  isMediaVideo,
  isResolvingGalleryContent,
  isSlideshowActive,
  isZipDownloading,
  linkingTargetIsGooncave,
  openEditorsAfterSelection,
  post,
  setImageDisplayMode,
  setIsAddToScriptsDropdownOpen,
  setIsFindSimilarDropdownOpen,
  setIsGoonfolioDropdownOpen,
  setIsSlideshowActive,
  setShowInfo,
  setShowSaveToGoonfolio,
  setSlideshowInterval,
  setSlideshowShuffle,
  showInfo,
  slideshowInterval,
  slideshowShuffle,
  topToolbarRef,
  zipDownloadProgress,
}: TopToolbarProps) {
  const tt = useTranslations("booruDetailModal")
  return (
    <>
      {!hideUI && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1.5 max-[420px]:w-[calc(100vw-96px)]">
                <div
                  ref={topToolbarRef}
                  // This pill can hold 13+ icon buttons depending on which
                  // conditional ones are active (script status, album zip,
                  // fullscreen, display mode, save-to-goonfolio, add-to-
                  // editor, screenshot, etc.) and had no responsive handling
                  // at all — on a narrow viewport it simply overflowed its
                  // centered position and got clipped by the media
                  // container's overflow-hidden. Scaling the whole pill down
                  // as one unit (rather than touching every individual
                  // button's size) keeps every icon visible and in order,
                  // just smaller — matches "should scale down to fit"
                  // instead of silently dropping icons.
                  // Phones (420px and narrower): shrinking the pill to 68% made every button about 20px, too small to tap. There the buttons keep a
                  // thumb-sized 40px and the pill scrolls sideways instead, every button still present and in order.
                  className="flex items-center gap-1.5 bg-black/60 rounded-full px-2 py-1.5 origin-top max-[520px]:scale-[0.82] max-[420px]:scale-100 max-[420px]:w-full max-[420px]:max-w-max max-[420px]:overflow-x-auto max-[420px]:justify-start max-[420px]:[scrollbar-width:none] max-[420px]:pr-8 max-[420px]:[mask-image:linear-gradient(to_right,black_82%,transparent)] max-[420px]:[&_button]:h-10 max-[420px]:[&_button]:w-10 max-[420px]:[&_button]:shrink-0 max-[420px]:[&_svg]:h-5 max-[420px]:[&_svg]:w-5"
                  onClick={(e) => e.stopPropagation()}
                  // The viewer reads finger swipes anywhere on it (next/previous post, open the info panel). A finger sliding along this toolbar
                  // (it scrolls sideways on phones) must not also count as one of those.
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[30px] w-[30px] rounded-[9999px] text-white/80 hover:text-white hover:bg-white/10 pointer-events-auto flex items-center justify-center focus-visible:rounded-[9999px] focus-visible:ring-offset-0"
                    style={{ borderRadius: 9999 }}
                    onClick={() => setShowInfo(!showInfo)}
                    title={tt("infoI")}
                  >
                    <Info className="h-4 w-4" />
                  </Button>

                  {/* Script Status Icon */}
                  {activeScriptId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-[30px] w-[30px] text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded-full animate-pulse pointer-events-auto flex items-center justify-center"
                      title={`Script Playing: ${activeScriptName}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        // Could toggle mini player visibility or show script settings
                      }}
                    >
                      <ScrollText className="h-4 w-4 fill-current" />
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-[30px] w-[30px] hover:bg-white/10 rounded-full pointer-events-auto flex items-center justify-center ${
                      post && checkIsFavorite(`${post.site}-${post.id}`)
                        ? "text-red-500 hover:text-red-400"
                        : "text-white/80 hover:text-white"
                    }`}
                    onClick={handleFavoriteClick}
                    title={tt("favorite")}
                  >
                    <Heart
                      className={`h-4 w-4 ${post && checkIsFavorite(`${post.site}-${post.id}`) ? "fill-current" : ""}`}
                    />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full pointer-events-auto flex items-center justify-center"
                    onClick={handleDownload}
                    title={isAlbum ? tt("downloadCurrentPage") : tt("download")}
                  >
                    <Download className="h-4 w-4" />
                  </Button>

                  {isAlbum && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full pointer-events-auto flex items-center justify-center disabled:opacity-50"
                      onClick={handleDownloadAllAsZip}
                      disabled={isZipDownloading}
                      title={tt("downloadAllPagesAsZIPExternalContentCan")}
                    >
                      {isZipDownloading ? (
                        <span className="text-[9px] font-semibold tabular-nums">{zipDownloadProgress}%</span>
                      ) : (
                        <FolderArchive className="h-4 w-4" />
                      )}
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full pointer-events-auto flex items-center justify-center"
                    onClick={handleShare}
                    title={tt("share")}
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>

                  {/* Image display mode toggle - only show for images (not videos or GIFs) */}
                  {!isMediaVideo && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full pointer-events-auto flex items-center justify-center"
                      onClick={() => setImageDisplayMode(prev => prev === 'contain' ? 'cover' : 'contain')}
                      title={imageDisplayMode === 'contain' ? tt("showFullResolutionScrollable") : tt("fitToScreen")}
                    >
                      {imageDisplayMode === 'contain' ? (
                        <MoveVertical className="h-4 w-4" />
                      ) : (
                        <Frame className="h-4 w-4" />
                      )}
                    </Button>
                  )}

                  {/*
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-white/80 hover:text-yellow-400 hover:bg-yellow-500/20 rounded-full"
                    onClick={() => setShowReportDialog(true)}
                    title="Report"
                  >
                    <Flag className="h-4 w-4" />
                  </Button>
                  */}

                  {!isLinkingMode && !commissionSelectionActive && !bountySelectionActive && (
                    <DropdownMenu open={isGoonfolioDropdownOpen} onOpenChange={setIsGoonfolioDropdownOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 rounded-full pointer-events-auto flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10"
                          title={tt("saveToGoonfolio")}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
                        >
                          <Folder className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault()
                            setShowSaveToGoonfolio(true)
                            setIsGoonfolioDropdownOpen(false)
                          }}
                        >
                          <FolderPlus className="h-4 w-4 mr-2" />
                          <span>{tt("saveToGoonfolio")}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault()
                            if (post) {
                              const { queue, addToQueue } = useGooncaveStore.getState()
                              const alreadyQueued = queue.some((qp) => qp.id === post.id && qp.site === post.site)
                              if (alreadyQueued) {
                                toast.info(tt("alreadyInGooncaveQueue"))
                              } else {
                                addToQueue(post)
                                toast.success(tt("addedToGooncaveQueue"))
                              }
                            }
                            setIsGoonfolioDropdownOpen(false)
                          }}
                        >
                          <SquaresFour className="h-4 w-4 mr-2" />
                          <span>{tt("addToGooncaveQueue")}</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {/*
                  CHANGE: Replace separate buttons with combined dropdown for editor selection
                  */}
                  {isLinkingMode ? (
                    /* In linking mode: single button, no dropdown, auto-routes back to source editor.
                       Disabled while an erome/doujins placeholder post is still resolving its real
                       gallery URLs — otherwise this grabs the search-result thumbnail instead of the
                       actual video/image (see the gallery-fetch effect and isResolvingGalleryContent). */
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={isResolvingGalleryContent}
                      className="h-7 w-7 rounded-full pointer-events-auto flex items-center justify-center text-pink-400 hover:text-pink-300 hover:bg-pink-500/20 animate-glow-breathe ring-2 ring-pink-500/60 disabled:opacity-50 disabled:animate-none"
                      title={isResolvingGalleryContent ? tt("resolvingContent") : linkingTargetIsGooncave ? tt("addToGooncave") : tt("addToEditor")}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!isResolvingGalleryContent) handleAddToScript() }}
                    >
                      {isResolvingGalleryContent ? <Loader2 className="h-4 w-4 animate-spin" /> : linkingTargetIsGooncave ? <SquaresFour className="h-4 w-4" /> : <PenTool className="h-4 w-4" />}
                    </Button>
                  ) : commissionSelectionActive || bountySelectionActive ? null : (
                  <DropdownMenu open={isAddToScriptsDropdownOpen} onOpenChange={setIsAddToScriptsDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-full pointer-events-auto flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10"
                        title={tt("addToEditor")}
                      >
                        <PenTool className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          handleAddToScript()
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <ScrollText className="h-4 w-4 mr-2" />
                        <span>{tt("addToScriptCreator")}</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            const postWithAlbum = {
                              ...post,
                              albumImages: effectiveAlbumImages || post.albumImages || [],
                              albumTitle: fetchedDoujinsTitle || post.title || post.albumTitle,
                            }
                            useScriptLinkingStore.setState({ returnToTab: "story-scripter", pendingPost: postWithAlbum, returnToEditor: true, isLinkingMode: false })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        <span>{tt("addToStoryScripter")}</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            const enriched = getEnrichedPost() || post
                            useScriptLinkingStore.setState({ returnToTab: "vertical-editor", pendingPost: enriched, returnToEditor: true, isLinkingMode: false })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <LayoutList className="h-4 w-4 mr-2" />
                        <span>{tt("addToVerticalEditor")}</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            const enriched = getEnrichedPost() || post
                            useScriptLinkingStore.setState({ returnToTab: "curve-editor", pendingPost: enriched, returnToEditor: true, isLinkingMode: false })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <Spline className="h-4 w-4 mr-2" />
                        <span>{tt("addToCurveEditor")}</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            const enriched = getEnrichedPost() || post
                            useScriptLinkingStore.setState({ returnToTab: "block-editor", pendingPost: enriched, returnToEditor: true, isLinkingMode: false })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <Grid3X3 className="h-4 w-4 mr-2" />
                        <span>{tt("addToBlockEditor")}</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            const enriched = getEnrichedPost() || post
                            useScriptLinkingStore.setState({ returnToTab: "manual-control", pendingPost: enriched, returnToEditor: true, isLinkingMode: false })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <SlidersHorizontal className="h-4 w-4 mr-2" />
                        <span>{tt("addToManualControl")}</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            const enriched = getEnrichedPost() || post
                            useScriptLinkingStore.setState({ returnToTab: "patterns", pendingPost: enriched, returnToEditor: true, isLinkingMode: false })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <WaveSine className="h-4 w-4 mr-2" />
                        <span>{tt("addToPatternLibrary")}</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            const enriched = getEnrichedPost() || post
                            useScriptLinkingStore.setState({ returnToTab: "playmate", pendingPost: enriched, returnToEditor: true, isLinkingMode: false })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        <span>{tt("addToPlaymate")}</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            const enriched = getEnrichedPost() || post
                            useScriptLinkingStore.setState({ returnToTab: "script-player", pendingPost: enriched, returnToEditor: true, isLinkingMode: false })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <PlayCircle className="h-4 w-4 mr-2" />
                        <span>{tt("addToScriptPlayer")}</span>
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault()
                          if (post) {
                            useScriptLinkingStore.setState({
                              returnToTab: "bounties",
                              pendingPost: post,
                              returnToEditor: true,
                              isLinkingMode: false,
                            })
                            closeModalSurfaces()
                            openEditorsAfterSelection()
                          }
                          setIsAddToScriptsDropdownOpen(false)
                        }}
                      >
                        <HeartHandshake className="h-4 w-4 mr-2" />
                        <span>{tt("createBounty")}</span>
                      </DropdownMenuItem>

                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}

                  {/* Screenshot / frame capture button */}
                  <Button
                    onClick={(e) => { e.stopPropagation(); handleCaptureFrame() }}
                    variant="ghost"
                    size="icon"
                    className={`h-7 w-7 rounded-full pointer-events-auto flex items-center justify-center transition-colors ${
                      capturedFrameDataUrl
                        ? "text-green-400 hover:text-green-300 hover:bg-green-500/20"
                        : "text-white/80 hover:text-white hover:bg-white/10"
                    }`}
                    title={capturedFrameDataUrl ? tt("frameCapturedClickToRecapture") : tt("captureFrameForReverseSearch")}
                    disabled={isCapturing}
                  >
                    {isCapturing ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-transparent" />
                    ) : capturedFrameDataUrl ? (
                      <div className="relative h-4 w-4">
                        <Aperture className="h-4 w-4" />
                        <div className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-green-400" />
                      </div>
                    ) : (
                      <Aperture className="h-4 w-4" />
                    )}
                  </Button>

                  {/* Captured frame preview + clear button */}
                  {capturedFrameDataUrl && (
                    <div className="relative h-7 w-7 pointer-events-auto">
                      <img
                        src={capturedFrameDataUrl}
                        alt={tt("capturedFrame")}
                        className="h-7 w-7 rounded object-cover ring-2 ring-green-400"
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); handleClearFrame() }}
                        className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-400"
                        title={tt("clearCapturedFrame")}
                      >
                        <X className="h-2 w-2" />
                      </button>
                    </div>
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        onClick={(e) => e.stopPropagation()}
                        variant="ghost"
                        size="icon"
                        className={`h-7 w-7 rounded-full pointer-events-auto flex items-center justify-center ${
                          capturedFrameDataUrl
                            ? "text-green-400 hover:text-green-300 hover:bg-green-500/20"
                            : "text-white/80 hover:text-white hover:bg-white/10"
                        }`}
                        title={capturedFrameDataUrl ? tt("reverseSearchUsingCapturedFrame") : tt("reverseSearch")}
                      >
                        <Camera className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {capturedFrameDataUrl && (
                        <>
                          <div className="flex items-center gap-2 px-2 py-1.5">
                            <img src={capturedFrameDataUrl} alt={tt("frame")} className="h-8 w-8 rounded object-cover ring-1 ring-green-400" />
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium">{tt("usingCapturedFrame")}</span>
                          </div>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem onClick={() => handleFindSource()}>{tt("findSource")}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleReverseSearch("imgops")}>{tt("imgOps")}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleReverseSearch("google")}>{tt("googleLens")}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleReverseSearch("yandex")}>{tt("yandexImages")}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleReverseSearch("saucenao")}>{tt("sauceNAO")}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleReverseSearch("iqdb")}>IQDB</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <p className="px-2 py-1.5 text-[11px] leading-snug text-muted-foreground">{tt("reverseSearchPasteHint")}</p>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu open={isFindSimilarDropdownOpen} onOpenChange={setIsFindSimilarDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full pointer-events-auto flex items-center justify-center"
                        title={tt("findSimilar")}
                      >
                        <Tags className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => handleFindSimilar("current")}>
                        {tt("findByTagsCurrentSet")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleFindSimilar("new")}>
                        {tt("findByTagsNewSet")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-7 w-7 rounded-full pointer-events-auto flex items-center justify-center ${
                      isSlideshowActive
                        ? "text-amber-400 hover:bg-amber-500/20"
                        : "text-white/80 hover:text-white hover:bg-white/10"
                    }`}
                    onClick={() => setIsSlideshowActive(!isSlideshowActive)}
                    title={tt("slideshow")}
                  >
                    <Presentation className="h-4 w-4" />
                  </Button>

                  {/* Picture-in-Picture Button */}
                  {isMediaVideo && activeVideoUrl && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/10 rounded-full pointer-events-auto"
                      onClick={handleOpenPip}
                      title={tt("pictureInPicture")}
                    >
                      <PictureInPicture2 className="h-4 w-4" />
                    </Button>
                  )}

                  {isSlideshowActive && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-7 w-7 rounded-full pointer-events-auto ${
                          slideshowShuffle
                            ? "text-purple-400 hover:bg-purple-500/20"
                            : "text-white/80 hover:text-white hover:bg-white/10"
                        }`}
                        onClick={() => setSlideshowShuffle(!slideshowShuffle)}
                        title={slideshowShuffle ? tt("shuffleOn") : tt("shuffleOff")}
                      >
                        {slideshowShuffle ? <Shuffle className="h-4 w-4" /> : <LayoutList className="h-4 w-4" />}
                      </Button>

                      <div className="relative group pointer-events-auto">
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="h-7 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-br from-white/15 to-white/5 hover:from-white/25 hover:to-white/15 border border-white/30 text-white/90 hover:shadow-xl"
                          title={tt("slideshowSpeed")}
                        >
                          {tt("valueS", { value: Math.floor(slideshowInterval / 1000) })}
                        </button>

                        <div className="absolute top-full mt-1.5 right-0 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200 z-50">
                          <div className="flex items-center gap-0.5 bg-gradient-to-br from-white/20 to-white/5 rounded-full border border-white/40 shadow-xl p-1">
                            {[1000, 2000, 3000, 5000, 10000].map((interval) => (
                              <button
                                key={interval}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSlideshowInterval(interval)
                                }}
                                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                                  slideshowInterval === interval
                                    ? "bg-gradient-to-r from-cyan-500/60 to-blue-500/60 text-white font-semibold"
                                    : "text-white/70 hover:text-white hover:bg-white/10"
                                }`}
                              >
                                {tt("valueS", { value: Math.floor(interval / 1000) })}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Wide "Add to X" CTA gets its own row below the icon row so it
                    doesn't push other buttons out of view when active. */}
                {commissionSelectionActive ? (
                  <Button
                    variant="default"
                    size="sm"
                    className="h-7 rounded-full pointer-events-auto gap-1.5 px-3 bg-black/60"
                    title={tt("addToCommission")}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleAddToCommissionSelection()
                    }}
                  >
                    <HeartHandshake className="h-3.5 w-3.5" />
                    <span>{tt("addToCommission")}</span>
                  </Button>
                ) : bountySelectionActive ? (
                  <Button
                    variant="default"
                    size="sm"
                    className="h-7 rounded-full pointer-events-auto gap-1.5 px-3 bg-black/60"
                    title={tt("addToBounty")}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleAddToBountySelection()
                    }}
                  >
                    <HeartHandshake className="h-3.5 w-3.5" />
                    <span>{tt("addToBounty")}</span>
                  </Button>
                ) : null}
                </div>
              )}
    </>
  )
}
